<div class="modal-header">
  <h2 class="modal-title fw-bold">{{ 'manageEvent.editPackageRegistration.title' | translate }}</h2>
  <button type="button" class="btn-close" (click)="close()"></button>
</div>

<div class="modal-body">
  <!-- Package Info -->
  <div class="alert alert-light-primary d-flex align-items-center mb-6">
    <i class="fa fa-box-open fs-2x text-primary me-3"></i>
    <div>
      <strong>{{ package.package_name }}</strong>
      <span class="text-muted ms-2">{{ formatPrice(package.price_cents, package.currency) }}</span>
      <span class="text-muted ms-2">({{ package.session_count }} {{ 'manageEvent.editPackageRegistration.sessions' | translate }})</span>
    </div>
  </div>

  <!-- Current Registration Info -->
  <div class="alert alert-info d-flex align-items-start mb-6">
    <i class="fa fa-info-circle fs-2x me-3 mt-1"></i>
    <div>
      <div class="fw-bold mb-2">{{ 'manageEvent.editPackageRegistration.currentRegistration' | translate }}</div>
      <div class="mb-1">
        <strong>{{ 'manageEvent.editPackageRegistration.participant' | translate }}:</strong>
        @if (registration.user) {
          {{ registration.user.first_name }} {{ registration.user.last_name }}
          @if (registration.user.email) {
            <span class="text-muted">({{ registration.user.email }})</span>
          }
        } @else {
          <span class="text-muted">{{ 'common.notAvailable' | translate }}</span>
        }
      </div>
      <div>
        <strong>{{ 'manageEvent.editPackageRegistration.horse' | translate }}:</strong>
        @if (registration.horse) {
          {{ registration.horse.name }}
          @if (registration.horse.breed) {
            <span class="text-muted">({{ registration.horse.breed }})</span>
          }
        } @else {
          <span class="text-muted">{{ 'manageEvent.editPackageRegistration.notSpecified' | translate }}</span>
        }
      </div>
    </div>
  </div>

  <form [formGroup]="form">
    <!-- User Search Field -->
    <div class="mb-5">
      <label class="form-label required">{{ 'manageEvent.editPackageRegistration.participant' | translate }}</label>
      <div class="position-relative">
        <div class="input-group">
          <input
            type="text"
            class="form-control"
            [(ngModel)]="userSearchTerm"
            [ngModelOptions]="{standalone: true}"
            (input)="onUserSearch()"
            [placeholder]="'manageEvent.editPackageRegistration.searchPlaceholder' | translate"
            [class.is-invalid]="userIdInvalid"
          />
          @if (selectedUser) {
            <button
              class="btn btn-outline-secondary"
              type="button"
              (click)="clearUserSearch()"
            >
              <i class="fa fa-times"></i>
            </button>
          }
        </div>
        @if (userIdInvalid) {
          <div class="text-danger fs-7 mt-1">{{ 'validation.participantRequired' | translate }}</div>
        }

        <!-- User Dropdown -->
        @if (filteredUsers.length > 0 && userSearchTerm && !selectedUser) {
          <div class="user-dropdown position-absolute w-100 bg-white border rounded shadow-sm mt-1" style="z-index: 1050; max-height: 300px; overflow-y: auto;">
            @for (user of filteredUsers; track user.user_id) {
              <div
                class="user-item p-3 cursor-pointer hover-bg-light border-bottom"
                (click)="selectUser(user)"
              >
                <div class="fw-semibold">{{ user.first_name }} {{ user.last_name }}</div>
                @if (user.email) {
                  <div class="text-muted fs-7">{{ user.email }}</div>
                }
              </div>
            }
          </div>
        }

        <!-- Selected User Display -->
        @if (selectedUser) {
          <div class="mt-3 p-4 bg-light rounded">
            <div class="fw-bold mb-2">{{ 'manageEvent.editPackageRegistration.selectedParticipant' | translate }}</div>
            <div class="row g-3">
              <div class="col-md-6">
                <div class="text-muted fs-7">{{ 'manageEvent.editPackageRegistration.name' | translate }}</div>
                <div class="fw-semibold">{{ selectedUser.first_name }} {{ selectedUser.last_name }}</div>
              </div>
              @if (selectedUser.email) {
                <div class="col-md-6">
                  <div class="text-muted fs-7">{{ 'manageEvent.editPackageRegistration.email' | translate }}</div>
                  <div class="fw-semibold">{{ selectedUser.email }}</div>
                </div>
              }
            </div>
          </div>
        }
      </div>
    </div>

    <!-- Horse Selector -->
    <div class="mb-5">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <label class="form-label mb-0">{{ 'manageEvent.editPackageRegistration.horseOptional' | translate }}</label>
        <button
          type="button"
          class="btn btn-sm btn-light-primary"
          (click)="openAddHorseModal()"
          [disabled]="!selectedUser"
        >
          <i class="fa fa-plus me-1"></i>
          {{ 'manageEvent.editPackageRegistration.addNewHorse' | translate }}
        </button>
      </div>
      @if (loadingHorses) {
        <div class="d-flex align-items-center">
          <div class="spinner-border spinner-border-sm me-2" role="status"></div>
          <span class="text-muted">{{ 'manageEvent.editPackageRegistration.loadingHorses' | translate }}</span>
        </div>
      } @else {
        @if (availableHorses.length > 0) {
          <app-selectorini
            [options]="availableHorses"
            [displayKey]="'name'"
            [valueKey]="'horse_id'"
            [placeholder]="'manageEvent.editPackageRegistration.selectHorsePlaceholder' | translate"
            [value]="form.get('horse_id')?.value"
            (valueChange)="onHorseChanged($event)"
            [searchable]="true"
          />
          <div class="text-muted fs-7 mt-1">
            {{ 'manageEvent.editPackageRegistration.showingHorses' | translate:{ count: availableHorses.length } }}
          </div>
          @if (selectedHorse) {
            <div class="mt-3 p-4 bg-light rounded">
              <div class="fw-bold mb-2">{{ 'manageEvent.editPackageRegistration.selectedHorseDetails' | translate }}</div>
              <div class="row g-3">
                <div class="col-md-6">
                  <div class="text-muted fs-7">{{ 'manageEvent.editPackageRegistration.name' | translate }}</div>
                  <div class="fw-semibold">{{ selectedHorse.name }}</div>
                </div>
                @if (selectedHorse.breed) {
                  <div class="col-md-6">
                    <div class="text-muted fs-7">{{ 'manageEvent.editPackageRegistration.breed' | translate }}</div>
                    <div class="fw-semibold">{{ selectedHorse.breed }}</div>
                  </div>
                }
                @if (selectedHorse.color) {
                  <div class="col-md-6">
                    <div class="text-muted fs-7">{{ 'manageEvent.editPackageRegistration.color' | translate }}</div>
                    <div class="fw-semibold">{{ selectedHorse.color }}</div>
                  </div>
                }
              </div>
            </div>
          }
        } @else {
          <div class="alert alert-warning d-flex align-items-center">
            <i class="fa fa-exclamation-triangle me-2"></i>
            <div>{{ 'manageEvent.editPackageRegistration.noHorsesFound' | translate }}</div>
          </div>
        }
      }
    </div>
  </form>

  <!-- Status -->
  <div class="mb-5">
    <label class="form-label">{{ 'manageEvent.editPackageRegistration.status' | translate }}</label>
    <select class="form-select" [(ngModel)]="status">
      <option value="pending">{{ 'manageEvent.editPackageRegistration.statusPending' | translate }}</option>
      <option value="confirmed">{{ 'manageEvent.editPackageRegistration.statusConfirmed' | translate }}</option>
      <option value="cancelled">{{ 'manageEvent.editPackageRegistration.statusCancelled' | translate }}</option>
    </select>
  </div>

  <!-- Payment Status -->
  <div class="mb-5">
    <label class="form-label">{{ 'manageEvent.editPackageRegistration.paymentStatus' | translate }}</label>
    <select class="form-select" [(ngModel)]="paymentStatus">
      <option value="not_required">{{ 'manageEvent.editPackageRegistration.paymentNotRequired' | translate }}</option>
      <option value="pay_later">{{ 'manageEvent.editPackageRegistration.paymentPending' | translate }}</option>
      <option value="paid">{{ 'manageEvent.editPackageRegistration.paymentPaid' | translate }}</option>
      <option value="refunded">{{ 'manageEvent.editPackageRegistration.paymentRefunded' | translate }}</option>
    </select>
  </div>

  <!-- Notes -->
  <div class="mb-5">
    <label class="form-label">{{ 'manageEvent.editPackageRegistration.notesOptional' | translate }}</label>
    <textarea
      class="form-control"
      rows="3"
      [placeholder]="'manageEvent.editPackageRegistration.notesPlaceholder' | translate"
      [(ngModel)]="notes"
    ></textarea>
  </div>
</div>

<div class="modal-footer">
  <app-button
    [variant]="'outline'"
    [label]="'common.cancel' | translate"
    (clicked)="close()"
    [disabled]="saving"
  />
  <app-button
    [variant]="'primary'"
    [label]="saving ? ('common.saving' | translate) : ('common.saveChanges' | translate)"
    [iconClass]="saving ? 'fa fa-spinner fa-spin' : 'fa fa-save'"
    (clicked)="save()"
    [disabled]="saving || form.invalid"
  />
</div>
